import type { Location } from 'react-router'

/** Куда вести после входа, если адрес возврата не задан или подозрителен. */
export const DEFAULT_ROUTE = '/'

/** Экраны до кабинета: возвращать на них после входа бессмысленно (P-AUTH-01#R-02). */
const AUTH_PATHS = ['/login', '/request-access', '/select-company', '/invite']

function isAuthPath(pathname: string): boolean {
  return AUTH_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

/**
 * Адрес возврата из `?from=` — только путь кабинета на этом же сайте.
 *
 * Строка разбирается тем же парсером, что у браузера: `/\evil.example` и `/<TAB>/evil.example`
 * он читает как `//evil.example`, то есть как чужой хост. Проверка префиксов такие строки
 * пропускала бы, и ссылка на вход уводила бы пользователя на чужой сайт после ввода пароля.
 */
export function safeReturnTo(from: string | null): string {
  if (!from?.startsWith('/')) return DEFAULT_ROUTE

  let url: URL
  try {
    url = new URL(from, window.location.origin)
  } catch {
    return DEFAULT_ROUTE
  }

  if (url.origin !== window.location.origin || isAuthPath(url.pathname)) return DEFAULT_ROUTE
  return `${url.pathname}${url.search}${url.hash}`
}

type ReturnLocation = Pick<Location, 'pathname' | 'search'> & { hash?: string }

/** `/login?from=/orders/14` — запоминает, откуда пришли, чтобы вернуть после входа. */
export function withReturnTo(path: string, location: ReturnLocation): string {
  const current = `${location.pathname}${location.search}${location.hash ?? ''}`
  if (current === DEFAULT_ROUTE || isAuthPath(location.pathname)) return path
  return `${path}?from=${encodeURIComponent(current)}`
}
